import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'
import produce from 'immer'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'

import { useToastContext } from '../../../../../../contexts'
import {
  DespatchAdvice,
  DespatchAdviceItem,
  PatchRequest,
  WebEdiDto,
} from '../../../../../../data/models'
import { useSelectFirstAndOnlyOption } from '../../../../../../hooks'
import { getHandleableError } from '../../../../../../utils/errors/isXiaError'
import { calculateBreakpointWidth } from '../../../../../../utils/helper/calculateBreakpointWidth'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../../utils/helper/validateFormikFields'
import { ConfigurableComponent } from '../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../ui-config'
import {
  requestPatchDespatchAdviceById,
  requestPutDespatchAdviceItemById,
} from '../../../../data'

interface Props {
  visible: boolean
  setVisible: (params: boolean) => void
  items: DespatchAdviceItem[] | null | undefined
  setSelectedItems: (params: any) => void
  id: WebEdiDto['Id']
}

interface FormValues {
  deliveryStatus: DespatchAdviceItem['DeliveryStatus']
  confirmationReasonCode?: string
  confirmationReasonDescription?: string
}

type FormFieldName =
  | 'deliveryStatus'
  | 'confirmationReasonCode'
  | 'confirmationReasonDescription'

export const Reject: React.FC<Props> = ({
  visible,
  setVisible,
  items,
  setSelectedItems,
  id,
}) => {
  const { t } = useTranslation()

  const { uiConfig: config } = useConfigContext()

  const { showToast } = useToastContext()
  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)

  const DELIVERY_STATUS_REJECTED_CODE: DespatchAdviceItem['DeliveryStatus'] =
    'COMPLETE'

  // ************************************************************
  // Formik
  // ************************************************************
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      deliveryStatus: undefined,
      confirmationReasonCode: undefined,
      confirmationReasonDescription: undefined,
    },
    validate: data => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'deliveryStatus',
          config: config.despatchAdvice.rejectDespatchAdviceItem.deliveryStatus,
        },
      ]

      // check confirmationCode
      // only check this when the user selects delivery status === COMPLETE
      if (formik.values.deliveryStatus === DELIVERY_STATUS_REJECTED_CODE) {
        fieldsToValidate.push(
          {
            fieldName: 'confirmationReasonCode',
            config:
              config.despatchAdvice.rejectDespatchAdviceItem
                .confirmationReasonCode,
          },
          {
            fieldName: 'confirmationReasonDescription',
            config:
              config.despatchAdvice.rejectDespatchAdviceItem
                .confirmationReasonDescription,
          },
        )
      }

      validateFormikFields(fieldsToValidate, data, errors)

      return errors
    },
    onSubmit: async formData => {
      // TODO: Einheitliches onSubmit überall erreichen?
      // deactivate the buttons
      setIsRequestRunning(true)

      try {
        // close the dialog and reset
        handleClose()

        // submit form
        await submitForm(formData)

        // show success toast
        showToast({
          style: {},
          closable: true,
          severity: 'success',
          summary: t('common.toasts.rejectItemSummary'),
          detail: t('common.toasts.rejectItemDetail', {
            count: items?.length,
          }),
        })
      } catch (error) {
        let handleableError = getHandleableError(error)
        let errorMessage = handleableError.message

        // show error toast
        showToast({
          style: {},
          closable: true,
          severity: 'error',
          sticky: true,
          summary: t('common.toasts.rejectItemFailedSummary'),
          detail: errorMessage,
        })
      } finally {
        setIsRequestRunning(false)
      }
    },
  })

  const isFormFieldValid = (name: FormFieldName) =>
    !(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    return (
      !isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    )
  }

  const handleClose = () => {
    setVisible(false) // hide the dialog
    formik.resetForm()
    setSelectedItems([]) // deselect the items in the table
  }

  const submitForm = (formData: FormValues) => {
    items?.forEach(item => {
      // make a copy of the item
      const newDespatchAdviceItem = produce(item, draft => {
        // Partial delivery
        if (formData.deliveryStatus === 'PARTLY') {
          draft.ConfirmationCode = 'MODIFIED'
          draft.DeliveryStatus = 'PARTLY'
        }

        // No delivery
        if (formData.deliveryStatus === 'COMPLETE') {
          draft.ConfirmationCode = 'REJECTED'
          draft.DeliveryStatus = 'COMPLETE'
          draft.ConfirmationReason!.Code = formData.confirmationReasonCode
          draft.ConfirmationReason!.Description =
            formData.confirmationReasonDescription
        }
      })

      // patch for Payload.ConfirmationCode
      const confirmationCode: DespatchAdvice['ConfirmationCode'] = 'MODIFIED'
      const patchData: PatchRequest<WebEdiDto<DespatchAdvice>> = [
        {
          op: 'replace',
          path: '/Payload/ConfirmationCode',
          value: confirmationCode,
        },
      ]

      requestPatchDespatchAdviceById(id, patchData)

      // put for despatchAdviceItem
      const response = requestPutDespatchAdviceItemById(
        id,
        item.Id,
        newDespatchAdviceItem,
      )

      // return response header
      return response
    })
  }

  // ************************************************************
  // Components
  // ************************************************************
  // deliveryStatus
  const deliveryStatusLabel = (
    <label
      htmlFor="deliveryStatus"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('deliveryStatus'),
      })}
    ></label>
  )

  useSelectFirstAndOnlyOption(
    'deliveryStatus',
    config.despatchAdvice.rejectDespatchAdviceItem.deliveryStatus.options,
    formik,
  )

  const deliveryStatusComponent = (
    <Dropdown
      id="deliveryStatus"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('deliveryStatus'),
      })}
      placeholder={t(
        'despatchAdvice.positions.table.edit.dialog.form.deliveryStatus.placeholder',
      )}
      value={formik.values.deliveryStatus}
      onChange={formik.handleChange}
      optionLabel="code"
      optionValue="code"
    />
  )

  const deliveryStatusErrorMsg = (
    <div>{getFormErrorMessage('deliveryStatus')}</div>
  )

  const deliveryStatusWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.rejectDespatchAdviceItem}
      component={deliveryStatusComponent}
      label={deliveryStatusLabel}
      errorMsg={deliveryStatusErrorMsg}
    />
  )

  // confirmationReasonCode
  const confirmationReasonCodeLabel = (
    <label
      htmlFor="confirmationReasonCode"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationReasonCode'),
      })}
    ></label>
  )

  useSelectFirstAndOnlyOption(
    'confirmationReasonCode',
    config.despatchAdvice.rejectDespatchAdviceItem.confirmationReasonCode
      .options,
    formik,
  )

  const confirmationReasonCodeComponent = (
    <Dropdown
      id="confirmationReasonCode"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationReasonCode'),
      })}
      value={formik.values.confirmationReasonCode}
      onChange={formik.handleChange}
      optionLabel="code"
      optionValue="code"
    />
  )

  const confirmationReasonCodeErrorMsg = (
    <div>{getFormErrorMessage('confirmationReasonCode')}</div>
  )

  const confirmationReasonCodeWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.rejectDespatchAdviceItem}
      component={confirmationReasonCodeComponent}
      label={confirmationReasonCodeLabel}
      errorMsg={confirmationReasonCodeErrorMsg}
    />
  )

  // confirmationReasonDescription
  const confirmationReasonDescriptionLabel = (
    <label
      htmlFor="confirmationReasonDescription"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationReasonDescription'),
      })}
    ></label>
  )

  const confirmationReasonDescriptionComponent = (
    <InputText
      id="confirmationReasonDescription"
      value={formik.values.confirmationReasonDescription}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationReasonDescription'),
      })}
    />
  )

  const confirmationReasonDescriptionErrorMsg = (
    <div>{getFormErrorMessage('confirmationReasonDescription')}</div>
  )

  const ConfirmationReasonDescriptionWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.rejectDespatchAdviceItem}
      component={confirmationReasonDescriptionComponent}
      label={confirmationReasonDescriptionLabel}
      errorMsg={confirmationReasonDescriptionErrorMsg}
    />
  )

  // header
  const header = <>{t(`common.titles.rejectItem`, { count: items?.length })}</>

  // footer
  const footer = (
    <>
      <Button
        label={t(`common.buttons.cancel`)}
        icon="pi pi-times"
        className="p-button-text w-full md:w-auto mb-1 md:mb-0"
        onClick={handleClose}
      />
      <Button
        label={t(`common.buttons.save`)}
        icon={'pi pi-check'}
        className="w-full md:w-auto"
        onClick={formik.submitForm}
        disabled={!formik.dirty}
        loading={isRequestRunning}
      />
    </>
  )

  return (
    <>
      <Dialog
        visible={visible}
        header={header}
        style={{ width: '30rem' }}
        breakpoints={{ [calculateBreakpointWidth(30)]: '90vw' }}
        footer={footer}
        onHide={handleClose}
        blockScroll
      >
        <div className="formgrid grid">
          <div className="col-12">{deliveryStatusWrapper}</div>

          {/* only show this when the user selects delivery status === COMPLETE */}
          {formik.values.deliveryStatus === DELIVERY_STATUS_REJECTED_CODE && (
            <>
              <div className="col-12">{confirmationReasonCodeWrapper}</div>
              <div className="col-12">
                {ConfirmationReasonDescriptionWrapper}
              </div>
            </>
          )}
        </div>
      </Dialog>
    </>
  )
}
